<template>
  <div class="filter-drawer" :class="{ 'open': isOpen }">
    <div class="filter-drawer-header">
      <h5>Filter Messages</h5>
      <button type="button" class="close" @click="closeDrawer">
        <span>&times;</span>
      </button>
    </div>
    <div class="filter-drawer-body">
      <!-- Customer Multiselect -->
      <div class="form-group">
        <label>Customer</label>
        <multiselect
          v-model="selectedCustomer"
          track-by="id"
          placeholder="Type to search customers"
          :custom-label="customer => getCustomerDisplayName(customer)"
          :options="availableCustomers"
          :searchable="true"
          :loading="isLoadingCustomers"
          :internal-search="false"
          @search-change="searchCustomers">
          <template slot="noResult">
            No customers found
          </template>
        </multiselect>
      </div>

      <div class="form-group">
        <label>Statement ID</label>
        <input
          class="form-control"
          type="text"
          v-model="filters.statementId"
          placeholder="Enter statement ID">
      </div>

      <!-- Balance Range Filters - Separate rows -->
      <div class="form-group">
        <label>Min Closing Balance</label>
        <input
          class="form-control"
          type="number"
          v-model="filters.balanceMin"
          placeholder="Minimum balance">
      </div>

      <div class="form-group">
        <label>Max Closing Balance</label>
        <input
          class="form-control"
          type="number"
          v-model="filters.balanceMax"
          placeholder="Maximum balance">
      </div>

      <!-- Date Range Filters - Separate rows -->
      <div class="form-group">
        <label>Generated From</label>
        <input
          class="form-control"
          type="date"
          v-model="filters.dateFrom">
      </div>

      <div class="form-group">
        <label>Generated To</label>
        <input
          class="form-control"
          type="date"
          v-model="filters.dateTo">
      </div>

      <!-- Action buttons -->
      <div class="filter-actions">
        <button class="btn btn-block btn-primary" @click="applyFilters">
          Apply Filters
        </button>
        <button class="btn btn-block btn-secondary mt-2" @click="resetFilters">
          Reset Filters
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import customerService from "../../service/customer.service";
import Multiselect from 'vue-multiselect';

export default {
  components: {
    Multiselect
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    initialFilters: {
      type: Object,
      default: () => ({
        searchFilterId: 'customerId',
        searchValue: '',
        recordsPerPage: 25
      })
    },
    searchFilters: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      filters: {
        searchFilterId: this.initialFilters.searchFilterId || 'customerId',
        searchValue: this.initialFilters.searchValue || '',
        recordsPerPage: this.initialFilters.recordsPerPage || 25, // Keep this for compatibility
        customerId: '',
        statementId: '',
        dateFrom: '',
        dateTo: '',
        balanceMin: '',
        balanceMax: ''
      },
      // Multiselect properties
      selectedCustomer: null,
      availableCustomers: [],
      isLoadingCustomers: false
    }
  },
  methods: {
    closeDrawer() {
      this.$emit('close')
    },

    getCustomerDisplayName(customer) {
      if (!customer) return '';
      return customer.name || `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || customer.id;
    },

    searchCustomers(query) {
      if (!query || query.length < 2) return;

      this.isLoadingCustomers = true;
      const merchantId = localStorage.getItem('mid');

      const params = {
        name: query,
        page: 1,
        size: 10,
        op: 'like'
      };

      customerService.find(merchantId, params)
        .then(response => {
          if (response.data && response.data.data) {
            this.availableCustomers = response.data.data;
          }
        })
        .catch(error => {
          console.error('Error searching customers:', error);
        })
        .finally(() => {
          this.isLoadingCustomers = false;
        });
    },

    // Filter methods
    applyFilters() {
      // Update customerId if a customer is selected
      if (this.selectedCustomer) {
        this.filters.customerId = this.selectedCustomer.id;
      }

      // Send back all filters to parent component
      this.$emit('filters-updated', {
        // Legacy filters for backward compatibility
        searchFilterId: this.filters.searchFilterId,
        searchValue: this.filters.customerId || this.filters.statementId || '',
        recordsPerPage: this.filters.recordsPerPage,

        // Advanced filters
        customerId: this.filters.customerId,
        statementId: this.filters.statementId,
        dateFrom: this.filters.dateFrom,
        dateTo: this.filters.dateTo,
        balanceMin: this.filters.balanceMin,
        balanceMax: this.filters.balanceMax
      })
    },

    resetFilters() {
      this.filters = {
        searchFilterId: this.initialFilters.searchFilterId || 'customerId',
        searchValue: '',
        recordsPerPage: this.initialFilters.recordsPerPage || 25,
        customerId: '',
        statementId: '',
        dateFrom: '',
        dateTo: '',
        balanceMin: '',
        balanceMax: ''
      };
      this.selectedCustomer = null;
    }
  },
  watch: {
    initialFilters: {
      handler(newVal) {
        if (newVal) {
          // Update only the properties that exist in initialFilters
          this.filters.searchFilterId = newVal.searchFilterId || this.filters.searchFilterId;
          this.filters.searchValue = newVal.searchValue || this.filters.searchValue;
          this.filters.recordsPerPage = newVal.recordsPerPage || this.filters.recordsPerPage;
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.filter-drawer {
  position: fixed;
  top: 0;
  right: -350px;
  width: 350px;
  height: 100%;
  background-color: #fff;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  transition: right 0.3s ease;
  z-index: 1050;
  overflow-y: auto;
}

.filter-drawer.open {
  right: 0;
}

.filter-drawer-header {
  padding: 15px;
  border-bottom: 1px solid #e5e5e5;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-drawer-body {
  padding: 15px;
}

.filter-actions {
  margin-top: 30px;
}
</style>



// WEBPACK FOOTER //
// src/components/communications/CommunicationsFilterDrawer.vue